import { useEffect, useState } from 'react';
import { auth as authApi } from '../../api/endpoints';
import { BrandScreen } from '../../components/layout/Screens';
import { SiroccoLogo } from '../../theme/illustrations';
import { useSession } from '../../store/SessionContext';
import { t } from '../../i18n';

/**
 * La porte de l'inscription : confirmer son adresse.
 *
 * Tant que le lien recu par courriel n'a pas ete suivi, il n'y a rien a
 * voir dans l'application. Ce n'est pas une formalite administrative :
 * une adresse non confirmee est une adresse que personne ne peut
 * reprendre, ni pour retrouver son mot de passe, ni pour prevenir avant
 * la suppression d'un compte endormi.
 *
 * Il n'y a rien a toucher pour passer : l'ecran le voit seul. Confirmer
 * se fait dans une autre application — la messagerie — et l'on revient
 * ici, ce qui suffit a redemander ; et tant qu'il est affiche, il
 * redemande toutes les quatre secondes, pour qui a suivi le lien sur un
 * autre appareil. Le bouton « J'ai confirme » ne servait qu'a cela, et
 * l'on se demandait s'il fallait le toucher.
 */

/** Le rythme auquel l'ecran redemande, tant qu'il est au premier plan. */
const REGARD_MS = 4000;
export function ConfirmerAdresseScreen() {
  const { user, logout } = useSession();

  const [verifie, setVerifie] = useState(false);
  const [envoi, setEnvoi] = useState<
    'calme' | 'en-cours' | 'parti' | 'echec' | 'patiente' | 'trop'
  >('calme');

  /*
    On redemande a Firebase, plutot que de croire le jeton en memoire :
    il ne se met pas a jour tout seul quand on suit le lien ailleurs.
  */
  async function regarder() {
    const confirme = await authApi.checkVerified().catch(() => false);
    setVerifie(confirme);
    return confirme;
  }

  useEffect(() => {
    void regarder();
    const auRetour = () => {
      if (!document.hidden) void regarder();
    };
    document.addEventListener('visibilitychange', auRetour);
    const regard = window.setInterval(() => {
      if (!document.hidden) void regarder();
    }, REGARD_MS);
    return () => {
      document.removeEventListener('visibilitychange', auRetour);
      window.clearInterval(regard);
    };
  }, []);

  /* Confirmee : la session se relit, et les gardes laissent passer. */
  useEffect(() => {
    if (verifie) window.location.reload();
  }, [verifie]);

  async function renvoyer() {
    setEnvoi('en-cours');
    try {
      // Rien n'est parti : le dire, plutot que de faire guetter un courriel.
      const retenu = await authApi.resendVerification();
      setEnvoi(retenu === 'repos' ? 'patiente' : retenu === 'plein' ? 'trop' : 'parti');
    } catch {
      setEnvoi('echec');
    }
  }

  return (
    <BrandScreen>
      <div className="auth auth--register">
        <div className="auth__logo">
          <SiroccoLogo markSize={50} />
        </div>

        <h1 className="auth__title">{t.auth.confirmer.titre}</h1>

        <p className="auth__switch auth__switch--top">
          {t.auth.confirmer.envoye.avant} <strong>{user?.email}</strong>
          {t.auth.confirmer.envoye.apres}
        </p>

        {envoi === 'parti' && <p className="auth__switch">{t.auth.confirmer.renvoye}</p>}
        {envoi === 'patiente' && <p className="auth__switch">{t.auth.confirmer.patiente}</p>}
        {envoi === 'trop' && <p className="auth__error">{t.auth.confirmer.trop}</p>}
        {envoi === 'echec' && <p className="auth__error">{t.auth.confirmer.echec}</p>}

        {/* La place du bouton parti : le renvoi ne se colle pas au texte. */}
        <button
          type="button"
          className="auth__link auth__link--detache"
          disabled={envoi === 'en-cours'}
          onClick={() => void renvoyer()}
        >
          {t.auth.confirmer.renvoyer}
        </button>

        <button type="button" className="auth__link" onClick={() => void logout()}>
          {t.auth.confirmer.deconnecter}
        </button>
      </div>
    </BrandScreen>
  );
}
